import React from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '../lib/utils'
import { Button } from './ui/button'

export interface ClearFiltersButtonProps {
  count: number
  onClear: () => void
  disabled?: boolean
  className?: string
  /** When false, hide if count is 0. Default: always visible. */
  alwaysVisible?: boolean
}

export function ClearFiltersButton({
  count,
  onClear,
  disabled,
  className,
  alwaysVisible = true,
}: ClearFiltersButtonProps) {
  const { t } = useTranslation()
  const inactive = count <= 0
  if (!alwaysVisible && inactive) return null

  const label = inactive ? t('ui.clearFiltersShort') : t('ui.clearFilters', { count })

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClear}
      disabled={disabled ?? inactive}
      className={cn(
        'mb-0.5 text-muted-foreground hover:text-destructive',
        inactive && 'opacity-50',
        className,
      )}
    >
      <X className="h-4 w-4 me-1" />
      {label}
    </Button>
  )
}

/** Count filter keys that differ from `defaultFilters`. */
export function countDeviationsFromDefaults(
  filters: Record<string, string>,
  defaultFilters: Record<string, string>,
  keys?: string[],
): number {
  const keysToCheck =
    keys ?? Array.from(new Set([...Object.keys(filters), ...Object.keys(defaultFilters)]))
  let count = 0
  for (const key of keysToCheck) {
    const value = (filters[key] ?? '').trim()
    const defaultValue = (defaultFilters[key] ?? '').trim()
    if (value !== defaultValue) count++
  }
  return count
}
